import ClientCardTile from '@/components/client/client-card-tile';
import CardFillDetailDialog from '@/components/client/card-fill-detail-dialog';
import CardTileBalance from '@/components/client/card-tile-balance';
import CardProviderIcon from '@/components/card-provider-icon';
import { Button } from '@/components/ui/button';
import type { ICard, ICardFillJob, IPhone } from '@/entities/client.entity';
import { hasActiveBalanceFetchJob, hasActiveCardJob, isCardBalanceFetchReady } from '@/lib/card-fill-meta';
import { requestCardBalance } from '@/lib/card-fill-actions';
import { formatCardNumber, resolveCardDisplayNumber, resolveCardProvider } from '@/lib/format-card';
import { cn } from '@/lib/utils';
import { CreditCardIcon, InfoIcon, Loader2Icon, RefreshCwIcon } from 'lucide-react';
import { useMemo, useState, type MouseEvent } from 'react';

function hasCardDisplayIdentity(card: ICard): boolean {
	return Boolean(card.number?.trim() || card.maskedNumber?.trim());
}

interface ClientCardsProps {
	cards: ICard[];
	phones?: IPhone[];
	cardFillJobs?: ICardFillJob[];
	onCopy: (text: string) => void;
	variant?: 'full' | 'compact';
}

function CompactCardItem({
	card,
	phones,
	cardFillJobs,
	onCopy,
}: {
	card: ICard;
	phones?: IPhone[];
	cardFillJobs?: ICardFillJob[];
	onCopy: (text: string) => void;
}) {
	const [detailOpen, setDetailOpen] = useState(false);
	const provider = resolveCardProvider(card);
	const displayNumber = formatCardNumber(card);
	const hasBalance = card.balance != null;
	const hasDate = Boolean(card.date?.trim());
	const isActive = hasActiveCardJob(cardFillJobs, card.id);
	const balanceRefreshing = hasActiveBalanceFetchJob(cardFillJobs, card.id);
	const canRefreshBalance = isCardBalanceFetchReady(card);
	const copyValue = resolveCardDisplayNumber(card);

	function handleNumberCopy(e: MouseEvent) {
		e.stopPropagation();
		if (copyValue !== '—') onCopy(copyValue);
	}

	return (
		<>
			<div
				className={cn(
					'rounded-md border border-border bg-muted/10 p-1.5',
					!hasBalance && 'opacity-80',
				)}
			>
				<div className='flex items-center gap-1.5 min-w-0'>
					<CardProviderIcon provider={provider} />
					<button
						type='button'
						className='min-w-0 flex-1 text-left text-sm font-semibold font-mono break-all hover:bg-accent/60 active:bg-accent rounded-xs px-0.5 cursor-pointer select-none'
						onClick={handleNumberCopy}
					>
						{displayNumber}
					</button>
					{!hasDate && hasBalance ? (
						<CardTileBalance balance={card.balance ?? 0} size='sm' onCopy={onCopy} className='shrink-0' />
					) : null}
					<div className='flex shrink-0 items-center gap-0.5'>
						{isActive ? <Loader2Icon size={12} className='animate-spin text-chart-3' /> : null}
						{canRefreshBalance ? (
							<Button
								type='button'
								variant='ghost'
								size='sm'
								className='h-6 w-6 p-0'
								disabled={balanceRefreshing}
								onClick={e => {
									e.stopPropagation();
									requestCardBalance(card.id);
								}}
							>
								{balanceRefreshing ? (
									<Loader2Icon size={12} className='animate-spin' />
								) : (
									<RefreshCwIcon size={12} />
								)}
							</Button>
						) : null}
						<Button
							type='button'
							variant='ghost'
							size='sm'
							className='h-6 w-6 p-0'
							onClick={e => {
								e.stopPropagation();
								setDetailOpen(true);
							}}
						>
							<InfoIcon size={12} />
						</Button>
					</div>
				</div>
				{hasDate ? (
					<div className='mt-0.5 flex items-baseline justify-between gap-2 min-w-0 pl-6'>
						<span className='text-[10px] text-muted-foreground tabular-nums truncate'>{card.date}</span>
						{hasBalance ? (
							<CardTileBalance balance={card.balance ?? 0} size='sm' onCopy={onCopy} />
						) : null}
					</div>
				) : null}
			</div>

			<CardFillDetailDialog
				target={{ mode: 'card', card, phones }}
				jobs={cardFillJobs}
				open={detailOpen}
				onOpenChange={setDetailOpen}
			/>
		</>
	);
}

export default function ClientCards({ cards, phones, cardFillJobs, onCopy, variant = 'full' }: ClientCardsProps) {
	const displayCards = useMemo(() => {
		const withBalance: ICard[] = [];
		const withoutBalance: ICard[] = [];

		for (const card of cards) {
			if (card?.balance != null) {
				withBalance.push(card);
			} else {
				withoutBalance.push(card);
			}
		}

		withBalance.sort((a, b) => (b.balance ?? 0) - (a.balance ?? 0));

		return [...withBalance, ...withoutBalance];
	}, [cards]);

	if (variant === 'compact') {
		const compactCards = displayCards.filter(hasCardDisplayIdentity);

		return (
			<div className='flex flex-col gap-1.5'>
				<div className='flex items-center gap-1 min-w-0'>
					<CreditCardIcon size={14} className='opacity-40 shrink-0' />
					<span className='text-sm text-muted-foreground'>
						Карты
						{compactCards.length > 0 ? <span className='tabular-nums'> ({compactCards.length})</span> : null}
					</span>
				</div>

				<div className='flex flex-col gap-1.5'>
					{compactCards.length ? (
						compactCards.map((card, i) => (
							<CompactCardItem
								key={card.id ?? i}
								card={card}
								phones={phones}
								cardFillJobs={cardFillJobs}
								onCopy={onCopy}
							/>
						))
					) : (
						<span className='opacity-60 text-sm'>Нет карт</span>
					)}
				</div>
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-1.5'>
			<div className='flex items-center gap-1 min-w-0'>
				<CreditCardIcon size={14} className='opacity-40 shrink-0' />
				<span className='text-sm text-muted-foreground'>
					Карты
					{displayCards.length > 0 ? <span className='tabular-nums'> ({displayCards.length})</span> : null}
				</span>
			</div>

			<div className='flex flex-col gap-2'>
				{displayCards.length ? (
					displayCards.map((card, i) => (
						<ClientCardTile
							key={card.id ?? i}
							card={card}
							phones={phones}
							cardFillJobs={cardFillJobs}
							onCopy={onCopy}
						/>
					))
				) : (
					<span className='opacity-60 text-sm'>Нет карт</span>
				)}
			</div>
		</div>
	);
}
